import React, { useMemo, useState } from 'react';
import CommonCard from '../../../global/card';
import CommonSkeletonLoader from '../../../global/loading';
import CommonPagination from '../../../global/pagination';
import SOURCES from '../../../../utils/constants';
import { useGenericQuery } from '../../../../queries/use-generic-query';
import { QUERY_KEYS } from '../../../../lib/query-constants';
import useDebounce from '../../../../utils/customHooks';
import EnhancedAutoresponseSentList from './EnhancedAutoresponseSentList';

const EnhanceAutoresponseSent = () => {
    const { DEFAULT_RECORDS_LISTING, DEFAULT_RECORDS_SEARCH } = SOURCES;

    const [searchAutoresponse, setSearchAutoresponse] = useState("");
    const [viewType, setViewType] = useState(() => {
        return localStorage.getItem("autoresponseSentViewType") || "LIST";
    });
    const [sort, setSort] = useState({ sortType: "", sortOrder: 1 });

    const [pagination, setPagination] = useState({
        offset: 0,
        limit: DEFAULT_RECORDS_LISTING,
        page: 1,
    });

    const debouncedSearchTerm = useDebounce(searchAutoresponse, 500);

    // Key params for list
    const keyParamsForList = {
        offset: pagination.offset,
        limit: pagination.limit,
        sortBy: sort.sortType,
        sortOrder: sort.sortOrder,
    };

    // Key params for search
    const searchKeyParams = {
        q: debouncedSearchTerm,
    };

    // Main data query
    const {
        data: autoresponseData,
        isLoading: autoresponseLoading,
        isFetching: autoresponseFetching
    } = useGenericQuery({
        endpoint: "reports/autoresponder/sent/list",
        queryKey: QUERY_KEYS.autoresponseSent.lists({ ...keyParamsForList }),
        queryParams: { ...keyParamsForList },
        enabled: !debouncedSearchTerm,
    });

    // Search data query
    const {
        data: searchData,
        isInitialLoading: searchDataLoading,
        isFetching: searchDataFetching
    } = useGenericQuery({
        endpoint: "reports/autoresponder/sent/search",
        queryKey: QUERY_KEYS.autoresponseSent.lists({ ...searchKeyParams }),
        queryParams: { ...searchKeyParams },
        enabled: !!debouncedSearchTerm,
    });

    // Determine final data
    const getFinalData = useMemo(() => {
        if (debouncedSearchTerm) {
            return searchData?.results || [];
        }
        return autoresponseData?.results || [];
    }, [autoresponseData, searchData, debouncedSearchTerm]);

    const getTotalRecords = useMemo(() => {
        if (debouncedSearchTerm) {
            return searchData?.totalRecords || 0;
        }
        return autoresponseData?.totalRecords || 0;
    }, [autoresponseData, searchData, debouncedSearchTerm]);

    const isLoading = autoresponseLoading || searchDataLoading;
    const isFetching = autoresponseFetching || searchDataFetching;
    const dataExist = getFinalData && getFinalData.length > 0;

    return (
        <CommonCard
            isSearch={{
                show: true,
                placeholder: "Search autoresponse sent...",
                searchInput: searchAutoresponse,
                setSearchInput: setSearchAutoresponse
            }}
            isListGridView={{
                show: true,
                viewType: viewType,
                setViewType: setViewType,
                viewFor: "autoresponseSentViewType"
            }}
            isLoading={{
                show: isLoading,
                loaderCmp: (
                    <CommonSkeletonLoader
                        type={viewType === "GRID" ? "card" : "table"}
                    />
                ),
            }}
            className={(!isLoading && isFetching) ? "opacity-50" : ""}
        >
            <EnhancedAutoresponseSentList
                isLoading={isLoading}
                data={getFinalData}
                sort={sort}
                setSort={setSort}
                viewType={viewType}
            />

            {(dataExist && !searchAutoresponse) &&
                getTotalRecords > DEFAULT_RECORDS_LISTING && (
                    <CommonPagination
                        currentPage={pagination.page}
                        defaultLimit={pagination.limit}
                        totalRecords={getTotalRecords}
                        setCurrentPage={setPagination}
                        activePageRecords={getFinalData?.length || 0}
                        paginationFor="Autoresponse Sent"
                        className="mt-4"
                    />
                )}
        </CommonCard>
    );
};

export default EnhanceAutoresponseSent;